import * as VisuallyHiddenPrimitive from '@radix-ui/react-visually-hidden';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { AvatarNameDescription } from '../../../design-system/avatar/avatar-name-description/avatar-name-description';
import { Button } from '../../../design-system/button/button';
import { Input } from '../../../design-system/input/input';
import { Modal, ModalContent, ModalDescription, ModalFooter, ModalTitle } from '../../../design-system/modal/modal';
import { Stack } from '../../../design-system/stack/stack';
import { VendorView } from '../../../types/entity-types';
import { cn } from '../../../utils/cn';

type SelectedVendor = {
  id: string;
  name: string;
};

type ChangeVendorModalProps = {
  isOpen: boolean;
  onClose: () => void;
  vendors: VendorView[];
  onChangeVendor: (vendorId: string) => void;
};

export const ChangeVendorModal = ({ isOpen, onClose, vendors, onChangeVendor }: ChangeVendorModalProps) => {
  const [search, setSearch] = useState('');
  const [selectedVendor, setSelectedVendor] = useState<SelectedVendor | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const filteredVendors = vendors.filter((v) => v.name.toLowerCase().includes(search.toLowerCase()));

  const onCloseModal = () => {
    onClose();
    setSelectedVendor(null);
  };

  return (
    <Modal open={isOpen} onOpenChange={onCloseModal}>
      <ModalContent className="h-[600px] w-[400px]">
        <VisuallyHiddenPrimitive.Root asChild>
          <div>
            <ModalTitle>Change vendor category</ModalTitle>

            <ModalDescription>Modal to change the vendor category</ModalDescription>
          </div>
        </VisuallyHiddenPrimitive.Root>

        {/* TODO: maybe we move the min-w-0 to the stack component? */}
        <Stack justify="between" align="stretch" className="min-w-0">
          <Stack align="stretch" className="h-full">
            <div className="p-4">
              <Input
                leftIcon={<Search size={16} />}
                placeholder="Search vendors"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                clearable
                onClear={() => setSearch('')}
                // Prevents j/k from triggering transaction navigation while searching
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>

            <div className="relative h-full min-h-0">
              <Stack className="absolute inset-0 overflow-auto">
                {filteredVendors.map((v) => {
                  return (
                    <Button
                      key={v.id}
                      variant="ghost"
                      rounded={false}
                      size="none"
                      className="w-full *:w-full"
                      onClick={() => {
                        if (selectedVendor?.id === v.id) {
                          setSelectedVendor(null);

                          return;
                        }

                        setSelectedVendor({ id: v.id, name: v.name });
                      }}
                    >
                      <AvatarNameDescription
                        key={v.id}
                        avatar={{ showAvatar: true, avatarUrl: v.logo }}
                        name={v.name}
                        description={v.description ?? ''}
                        className={cn('w-full p-4', {
                          'bg-primary/20 hover:bg-primary/20': selectedVendor?.id === v.id,
                        })}
                      />
                    </Button>
                  );
                })}
              </Stack>
            </div>
          </Stack>

          <ModalFooter>
            <Button
              onClick={async () => {
                if (!selectedVendor) return;

                setIsLoading(true);
                await onChangeVendor(selectedVendor.id);
                setIsLoading(false);
              }}
              disabled={!selectedVendor}
              isLoading={isLoading}
              className="flex-1"
            >
              {selectedVendor ? `Select ${selectedVendor.name}` : 'Select vendor'}
            </Button>

            <Button variant="outline" onClick={onCloseModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Stack>
      </ModalContent>
    </Modal>
  );
};
